import isEmpty from "lodash.isempty";
import { paymentService } from "../services/payment";
import { JOB_ENERGYTYPE } from "../constants";
import useApi from "./useApi";
import useEnv from "./useEnv";

/**
 * Custom hook to handle job related actions
 *
 * @param {object|null} job
 * @returns {{
 *   fetchJobPaymentUrl: function(string): Promise<string|null>,
 *   downloadJobPdf: function(string): Promise<void>,
 *   postFireSafetyEvent: function(string): Promise<any|null>,
 *   shouldConfirmFireSafety: boolean,
 *   hasPaymentActions: boolean,
 *   isCreditInvoice: boolean,
 *   isPaidILInvoice: boolean,
 *   lastPaymentFailedAt: null|Date,
 *   submitMandate: function(object): Promise<*|null>,
 *   submitPayPlan: function(number, string): Promise<*|null>
 *   submitPayDate: function(string): Promise<*|null>
 * }}
 */
const useJob = (job) => {
    const { getJson, getBlob, postJson, patchJson } = useApi();
    const { isEnvTruthy } = useEnv();

    /**
     * Check if fire safety information should be asked before paying.
     * Fire safety information should be asked for jobs with statuscode 156 and energytype=electricity or null
     */
    const shouldConfirmFireSafety = job !== null &&
        job.statuscode === 156 &&
        isEnvTruthy('CONFIRM_FIRESAFETY') &&
        (job.energytype === JOB_ENERGYTYPE.ELECTRICITY || isEmpty(job.energytype));

    /**
     * Check if the job has a pending payment plan
     */
    const hasPendingPayplan = Array.isArray(job?.payplan) && job.payplan.some(plan => plan.isPending === true);

    const isPaidStatus = job?.statuscode === 101 || job?.statuscode === 122;
    const isPaidILInvoice = (job?.statuscode === 300 && !job?.makepayment);
    const isCreditInvoice = job?.statuscode === 104;

    /**
     * Check if payment actions (pay button, extend duedate, payment plan buttons) should be shown for the job
     */
    const hasPaymentActions = !isPaidStatus && !hasPendingPayplan && !isPaidILInvoice && !isCreditInvoice;

    const payments = [...(job?.payments ?? [])].sort((a, b) => (
        Date.parse(b.created_at) - Date.parse(a.created_at)
    ));

    /**
     * Check when last payment has failed or null if not
     * @type {null|Date}
     */
    const lastPaymentFailedAt = payments[0]?.status === 'failed' ? new Date(payments[0].updated_at) : null;

    /**
     * Posts a fire safety event to the server
     * @param {string} phoneNumber
     * @returns {Promise<any|null>}
     */
    const postFireSafetyEvent = async (phoneNumber) => {
        if (job === null || job.dpid === undefined) {
            return null;
        }

        const data = {
            dpid: job.dpid,
            phonenumber: phoneNumber
        };

        return await postJson('/online/event/firesafety', JSON.stringify({ data }));
    }

    /**
     * Fetches the payment URL for the job
     *
     * @param {string} bankId
     * @param {string} installmentAmount 
     * @returns {Promise<string|null>}
     */
    const fetchJobPaymentUrl = async (bankId, installmentAmount, phone) => {
        if (job === null) {
            return null;
        }

        let bankparam = '';

        // add bank id to query parameters if given
        if (bankId !== undefined && bankId !== '') {
            bankparam = '&bank_id=' + bankId;
        }

        let installmentparam = '';

        if (installmentAmount !== undefined && installmentAmount !== '') {
            installmentparam = '&pay_installment=' + installmentAmount;
        }

        const dpid = job.dpid ?? 'token';
        const service = paymentService.getPaymentService();

        // Store relevant information in local storage to be used when user is redirected back to the app after payment
        const paymentRedirectState = {
            jobPrevStatus: job.statuscode,
            jobDpid: job.dpid,
            service: service,
            state: null
        }

        let phoneparam = '';
        // add phone number for Worldline Vipps payments
        if (service === paymentService.PAYMENTMETHODS.WORLDLINE && bankId === 'Vipps') {
            phoneparam = `&phone=${phone}`;
        }

        const data = await getJson(`/online/registerpayment/${dpid}` +
            `?service=${service}${bankparam}${installmentparam}${phoneparam}`);

        // Store state in local storage to be used for re-authentication after payment, if needed
        if (data?.state) {
            paymentRedirectState.state = data.state;
        }

        window.localStorage.setItem('paymentRedirectState', JSON.stringify(paymentRedirectState));

        return data?.url ?? null;
    }

    /**
     * Downloads the job PDF
     *
     * @param {string} letterType
     * @returns {Promise<void>}
     */
    const downloadJobPdf = async (letterType) => {
        if (job === null || job.dpid === undefined) {
            return;
        }

        const jobIdsToDownload = [job.dpid];
        if (Array.isArray(job.members) && letterType !== 'current') {
            job.members.forEach(member => {
                if (member?.dpid) {
                    jobIdsToDownload.push(member.dpid);
                }
            });
        }

        let requestParams = '';
        if (letterType === 'invoice') {
            requestParams = '?origtype=1&lettertype=' + letterType;
        } else {
            requestParams = '?origtype=0&lettertype=' + letterType + '&jobtype=' + job.current_letter?.jobtype + '&jobstatus=' + job.current_letter?.jobstatus;
        }

        for (const id of jobIdsToDownload) {
            const data = await getBlob('/online/pdf/' + id + requestParams, 'application/pdf');
            if (data === null) {
                continue;
            }
            
            // Creates an empty 'a' element that allows manipulating the file name
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.style = "display: none";

            // Creates a BLOB object
            let newBlob = new Blob([data], { type: "application/pdf" });
            // Creates a new link
            const url = window.URL.createObjectURL(newBlob);

            a.href = url;
            a.download = "invoice_" + id + '.pdf';
            a.click();

            // Opens the link
            setTimeout(function () {
                window.URL.revokeObjectURL(url);
            }, 100);
        }
    }

    /**
     * Submits a mandate for the job
     * @param {object} authorizedPerson
     * @returns {Promise<*|null>}
     */
    const submitMandate = async (authorizedPerson) => {
        if (job === null || job.dpid === undefined) {
            return;
        }

        const data = JSON.stringify({
            id: job.dpid,
            name: authorizedPerson.name,
            phone: authorizedPerson.phone,
            email: authorizedPerson.email,
            user: {
                name: "",
                personid: ""
            }
        })

        return await postJson('/online/mandate', data);
    }

    /**
     * Submits a payment plan for the job
     * @param {number} paymentCount
     * @param {string} paymentInterval
     * @returns {Promise<*|null>}
     */
    const submitPayPlan = async (paymentCount, paymentInterval) => {
        if (job === null || job.dpid === undefined) {
            return;
        }

        const data = JSON.stringify({
            id: job.dpid,
            firstpaydate: job.firstpaydate,
            count: paymentCount,
            interval: paymentInterval
        })

        return await postJson('/online/payplan', data);
    }

    const submitPayDate = async (paymentDate) => {
        if (job === null || job.dpid === undefined) {
            return;
        }

        const data = JSON.stringify({
            id: job.dpid,
            dataset: {
                paydate: paymentDate
            }
        })

        return await patchJson('/online', data);
    }

    return {
        fetchJobPaymentUrl,
        downloadJobPdf,
        postFireSafetyEvent,
        shouldConfirmFireSafety,
        hasPaymentActions,
        isCreditInvoice,
        isPaidILInvoice,
        lastPaymentFailedAt,
        submitMandate,
        submitPayPlan,
        submitPayDate
    }
}

export default useJob
